import React, { useState, useEffect } from 'react';
import { 
  Users, Search, Building2, Mail, Phone, MapPin, 
  Calendar, CreditCard, TrendingUp, ChevronDown, 
  ChevronUp, UserCheck, UserX, Activity
} from 'lucide-react';
import axios from 'axios';
import api from '../services/api';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function MasterUsers() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedCompany, setExpandedCompany] = useState(null);

  useEffect(() => {
    loadCompanies();
  }, [search]);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const params = search ? `?search=${search}` : '';
      const res = await api.get(`/api/master-admin/users/companies${params}`);
      setCompanies(res.data);
    } catch (err) {
      console.error('Erro ao carregar empresas:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (companyId) => {
    setExpandedCompany(expandedCompany === companyId ? null : companyId);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-700',
      suspended: 'bg-orange-100 text-orange-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Ativa',
      suspended: 'Suspensa',
      cancelled: 'Cancelada'
    };
    return labels[status] || status;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Administradores das Empresas</h1>
        <p className="text-gray-500">Gerencie todos os admins e dados de contato das empresas</p>
      </div>

      {/* Busca */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Buscar por empresa, responsável ou email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
        />
      </div>

      {/* Lista de Empresas */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-6 py-4 font-semibold">Empresa</th>
                <th className="px-6 py-4 font-semibold">Administrador</th>
                <th className="px-6 py-4 font-semibold">Plano</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-400">
                    <div className="flex items-center justify-center gap-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                      <span>Carregando...</span>
                    </div>
                  </td>
                </tr>
              ) : companies.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-400">
                    <Building2 size={48} className="mx-auto mb-3 opacity-30" />
                    <p>Nenhuma empresa encontrada</p>
                  </td>
                </tr>
              ) : (
                companies.map(company => {
                  const isExpanded = expandedCompany === company.id;
                  const hasAdmin = !!company.admin_id;
                  
                  return (
                    <React.Fragment key={company.id}>
                      <tr className="hover:bg-gray-50">
                        {/* Empresa */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 text-white rounded-full flex items-center justify-center font-bold">
                              {company.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{company.name}</p>
                              <p className="text-xs text-gray-500">
                                CNPJ: {company.cnpj || '—'}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Administrador */}
                        <td className="px-6 py-4">
                          {hasAdmin ? (
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center">
                                <UserCheck size={14} />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{company.admin_name}</p>
                                <p className="text-xs text-gray-500">{company.admin_email}</p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center">
                                <UserX size={14} />
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Admin não cadastrado</p>
                                <p className="text-xs text-gray-400">Aguardando registro</p>
                              </div>
                            </div>
                          )}
                        </td>

                        {/* Plano */}
                        <td className="px-6 py-4">
                          <div>
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700">
                              <CreditCard size={12} className="mr-1" />
                              {company.plan_name || company.plan_type || 'Básico'}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              R$ {Number(company.plan_price || company.plan_value || 0).toFixed(2)}/mês
                            </p>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(company.status)}`}>
                            {getStatusLabel(company.status)}
                          </span>
                        </td>

                        {/* Ações */}
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => toggleExpand(company.id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                          >
                            {isExpanded ? (
                              <>
                                <ChevronUp size={14} />
                                Ocultar detalhes
                              </>
                            ) : (
                              <>
                                <ChevronDown size={14} />
                                Mostrar mais
                              </>
                            )}
                          </button>
                        </td>
                      </tr>

                      {/* Detalhes Expandidos */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={5} className="px-6 py-4 bg-gray-50/50">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              {/* Contato */}
                              <div className="bg-white rounded-xl p-4 border border-gray-200">
                                <h4 className="text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-2">
                                  <Building2 size={14} className="text-orange-600" />
                                  Dados da Empresa
                                </h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex items-start gap-2">
                                    <MapPin size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <p className="text-xs text-gray-500">Endereço</p>
                                      <p className="text-gray-900">{company.address || '—'}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Phone size={14} className="text-gray-400 flex-shrink-0" />
                                    <div>
                                      <p className="text-xs text-gray-500">Telefone</p>
                                      <p className="text-gray-900">{company.phone || '—'}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Mail size={14} className="text-gray-400 flex-shrink-0" />
                                    <div>
                                      <p className="text-xs text-gray-500">Email Responsável</p>
                                      <p className="text-gray-900 text-xs">{company.responsible_email || '—'}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Responsável */}
                              <div className="bg-white rounded-xl p-4 border border-gray-200">
                                <h4 className="text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-2">
                                  <UserCheck size={14} className="text-green-600" />
                                  Responsável
                                </h4>
                                <div className="space-y-2 text-sm">
                                  <div>
                                    <p className="text-xs text-gray-500">Nome</p>
                                    <p className="text-gray-900 font-medium">{company.responsible_name || '—'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Email</p>
                                    <p className="text-gray-900 text-xs">{company.responsible_email || '—'}</p>
                                  </div>
                                  {!hasAdmin && (
                                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                      <p className="text-xs text-yellow-800">
                                        ⚠️ Admin ainda não se registrou
                                      </p>
                                      <p className="text-xs text-yellow-600 mt-1">
                                        Código: <span className="font-mono font-bold">{company.invitation_code}</span>
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Estatísticas */}
                              <div className="bg-white rounded-xl p-4 border border-gray-200">
                                <h4 className="text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-2">
                                  <Activity size={14} className="text-blue-600" />
                                  Uso do Sistema
                                </h4>
                                <div className="space-y-3">
                                  <div>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                      <span className="text-gray-500">Usuários</span>
                                      <span className="font-bold text-gray-900">
                                        {company.total_users || 0} / {company.max_users || 5}
                                      </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div 
                                        className="bg-blue-600 h-2 rounded-full transition-all"
                                        style={{ 
                                          width: `${Math.min(((company.total_users || 0) / (company.max_users || 5)) * 100, 100)}%` 
                                        }}
                                      ></div>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500">Setores</span>
                                    <span className="font-bold text-gray-900">
                                      {company.total_sectors || 0} / {company.max_sectors || 5}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500">WhatsApp</span>
                                    <span className="font-bold text-gray-900">
                                      {company.total_phone_numbers || 0} / {company.whatsapp_numbers_limit || 3}
                                    </span>
                                  </div>
                                  <div className="pt-2 border-t border-gray-100">
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                      <Calendar size={12} />
                                      <span>Criada em: {new Date(company.created_at).toLocaleDateString('pt-BR')}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default MasterUsers;